import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MailWarning, RefreshCw, RotateCcw } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_CLS = {
  sent: "bg-emerald-500/90 text-black",
  failed: "bg-red-500/90 text-white",
  skipped: "bg-zinc-800 text-zinc-400",
};

// Human labels for the proxy's machine failure codes, so a spam-guard block
// never reads like a rate-limit (and vice versa). Raw error stays in the tooltip.
const FAILURE_LABELS = {
  undeliverable_recipient: { text: "Blocked — address looks undeliverable (spam guard)", cls: "text-amber-400/90" },
  rate_limited: { text: "Rate limited by email service (auto-retried)", cls: "text-red-400/80" },
  proxy_error: { text: "Email service error (auto-retried)", cls: "text-red-400/80" },
  guardrail_block: { text: "Blocked by content guardrail", cls: "text-amber-400/90" },
  network_error: { text: "Network error reaching email service", cls: "text-red-400/80" },
  client_error: { text: "Rejected by email service", cls: "text-red-400/80" },
};

export default function AdminEmail() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [retrying, setRetrying] = useState(null); // log id currently retrying
  const [ab, setAb] = useState(null); // digest timing experiment (A = Mon 9am ET, B = Mon 4pm ET)
  const [tab, setTab] = useState("email"); // email | sms
  const [smsRows, setSmsRows] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsFetched, setSmsFetched] = useState(false); // lazy: only query when the tab is opened

  const loadSms = async () => {
    setSmsLoading(true);
    try {
      const { data } = await api.get("/admin/sms-log");
      setSmsRows(data);
      setSmsFetched(true);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSmsLoading(false);
    }
  };

  const switchTab = (t) => {
    setTab(t);
    if (t === "sms" && !smsFetched) loadSms();
  };

  useEffect(() => {
    api.get("/admin/digest-ab").then((r) => setAb(r.data)).catch(() => setAb({}));
  }, []);

  const load = async (spin = false, status = statusFilter, q = search) => {
    if (spin) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/email-log", { params: { status, q } });
      setRows(data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // debounced recipient search
  useEffect(() => {
    const t = setTimeout(() => load(false, statusFilter, search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const retry = async (r) => {
    if (retrying) return;
    setRetrying(r.id);
    try {
      await api.post(`/admin/email-log/${r.id}/retry`);
      toast.success(`Retry queued for ${Array.isArray(r.to) ? r.to.join(", ") : r.to} — the new attempt will appear here shortly.`);
      setTimeout(() => load(), 6000); // background send takes a few seconds under rate limits
    } catch (err) {
      toast.error(apiErrorMessage(err, "Retry failed."));
    } finally {
      setRetrying(null);
    }
  };

  if (user && user.role !== "admin") {
    return <div className="p-14 text-red-400 uppercase text-sm">Admin only.</div>;
  }

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-6xl mx-auto" data-testid="admin-email-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Admin</span>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">Email / SMS log</h1>
          <p className="text-sm text-zinc-500 mt-3" data-testid="email-log-count">
            {tab === "email"
              ? `${rows.length} most recent send attempt${rows.length === 1 ? "" : "s"} — password resets, bid alerts, digests.`
              : `${smsRows.length} most recent text${smsRows.length === 1 ? "" : "s"} — broadcasts, bid alerts, STOP/START events.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "sms" && (
            <button
              onClick={loadSms}
              data-testid="sms-log-refresh"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/15 text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${smsLoading ? "animate-spin" : ""}`} /> Refresh
            </button>
          )}
          {tab === "email" && (
        <button
          onClick={() => load(true)}
          data-testid="email-log-refresh"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/15 text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
          )}
        </div>
      </div>

      <div className="flex gap-1.5 mb-5" data-testid="log-tabs">
        {[["email", "Email"], ["sms", "SMS"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => switchTab(key)}
            data-testid={`log-tab-${key}`}
            className={`text-[11px] uppercase tracking-widest font-bold px-4 py-2 border transition-colors ${
              tab === key
                ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                : "border-white/10 text-zinc-400 hover:border-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "sms" && (
        smsLoading && !smsFetched ? (
          <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading SMS log…</div>
        ) : smsRows.length === 0 ? (
          <div className="border border-dashed border-white/[0.08] p-12 text-center bg-[#0F0F10]" data-testid="sms-log-empty">
            <MailWarning className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <div className="font-display text-2xl font-black uppercase">No texts recorded yet</div>
            <p className="text-zinc-500 text-sm mt-2">Broadcasts from User Management land here with their message and recipient count.</p>
          </div>
        ) : (
          <div className="border border-white/[0.08] bg-[#0F0F10] overflow-x-auto" data-testid="sms-log-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-white/[0.08] text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold text-left">
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Message</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {smsRows.map((r) => {
                  const isBroadcast = r.kind === "broadcast_summary";
                  return (
                    <tr key={r.id} data-testid={`sms-log-row-${r.id}`} className={`hover:bg-white/[0.03] transition-colors ${isBroadcast ? "bg-[#FF3B30]/[0.04]" : ""}`}>
                      <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-zinc-300">
                        {isBroadcast ? (
                          <span data-testid={`sms-broadcast-recipients-${r.id}`}>
                            All drivers <span className="text-zinc-500">· {r.recipient_count} recipient{r.recipient_count === 1 ? "" : "s"}</span>
                          </span>
                        ) : (
                          r.to
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isBroadcast ? (
                          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-black px-2 py-1 bg-[#FF3B30]/90 text-white">Broadcast</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">{(r.kind || "").replace(/_/g, " ")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-300 max-w-[280px] truncate" title={r.body}>{r.body}</td>
                      <td className="px-4 py-3">
                        <span
                          data-testid={`sms-status-${r.id}`}
                          className={`inline-block text-[10px] uppercase tracking-[0.2em] font-black px-2 py-1 ${
                            r.status === "partial"
                              ? "bg-amber-500/90 text-black"
                              : STATUS_CLS[r.status] || STATUS_CLS.skipped
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-zinc-500 max-w-[220px] truncate" title={r.error || r.sid || ""}>
                        {isBroadcast ? (
                          <span data-testid={`sms-broadcast-breakdown-${r.id}`}>
                            {r.sent} sent · {r.failed} failed · {r.skipped} skipped{r.audience && r.audience !== "all" ? ` · ${r.audience} audience` : ""}{r.sent_by ? ` · by ${r.sent_by}` : ""}
                          </span>
                        ) : r.status === "sent" ? (
                          <span className="text-emerald-500/80">{r.sid}</span>
                        ) : (
                          <span className="text-red-400/80">{r.error}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "email" && (
      <>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex gap-1.5" data-testid="email-status-filters">
          {["", "sent", "failed", "skipped"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              data-testid={`email-filter-${s || "all"}`}
              className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors ${
                statusFilter === s
                  ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                  : "border-white/10 text-zinc-400 hover:border-white"
              }`}
            >
              {s || "All"}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by recipient email…"
          data-testid="email-search"
          className="flex-1 min-w-[220px] max-w-sm bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 py-2 px-3 text-sm"
        />
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading email log…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-white/[0.08] p-12 text-center bg-[#0F0F10]">
          <MailWarning className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <div className="font-display text-2xl font-black uppercase">No sends recorded yet</div>
          <p className="text-zinc-500 text-sm mt-2">Every email attempt lands here — sent, failed, or skipped.</p>
        </div>
      ) : (
        <div className="border border-white/[0.08] bg-[#0F0F10] overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="border-b border-white/[0.08] text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold text-left">
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Recipient</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Tries</th>
                <th className="px-4 py-3">Delivery id / error</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`email-log-row-${r.id}`} className="hover:bg-white/[0.03] transition-colors">
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-zinc-300">{Array.isArray(r.to) ? r.to.join(", ") : r.to}</td>
                  <td className="px-4 py-3 text-zinc-300 max-w-[260px] truncate">{r.subject}</td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`email-status-${r.id}`}
                      className={`inline-block text-[10px] uppercase tracking-[0.2em] font-black px-2 py-1 ${STATUS_CLS[r.status] || STATUS_CLS.skipped}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{r.attempts}</td>
                  <td className="px-4 py-3 text-[11px] text-zinc-500 max-w-[240px] truncate" title={r.error || r.email_id || ""}>
                    {r.status === "sent" ? (
                      <span className="text-emerald-500/80">{r.email_id}</span>
                    ) : r.failure_kind && FAILURE_LABELS[r.failure_kind] ? (
                      <span data-testid={`email-error-${r.id}`} className={FAILURE_LABELS[r.failure_kind].cls}>
                        {FAILURE_LABELS[r.failure_kind].text}
                      </span>
                    ) : (
                      <span data-testid={`email-error-${r.id}`} className="text-red-400/80">{r.error}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.status === "failed" && r.retryable && (
                      <button
                        onClick={() => retry(r)}
                        disabled={retrying === r.id}
                        data-testid={`retry-email-${r.id}`}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 border border-amber-500/50 text-amber-400 hover:bg-amber-500 hover:text-black transition-all disabled:opacity-50"
                      >
                        <RotateCcw className={`w-3 h-3 ${retrying === r.id ? "animate-spin" : ""}`} />
                        {retrying === r.id ? "Sending…" : "Resend"}
                      </button>
                    )}
                    {r.retried_at && (
                      <div className="text-[9px] uppercase tracking-widest text-zinc-600 mt-1" data-testid={`retried-mark-${r.id}`}>
                        resent {new Date(r.retried_at).toLocaleString()}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      <div className="mt-8 border border-white/[0.08] bg-[#0F0F10] p-5" data-testid="digest-ab-card">
        <h2 className="font-display text-xl font-black uppercase mb-1">Driver digest timing — A/B</h2>
        <p className="text-xs text-zinc-500 mb-3">
          Cohort A gets the Monday haul digest at 9am ET, cohort B at 4pm ET. Engaged = logged in within 24h of the digest.
        </p>
        {!ab || Object.keys(ab).length === 0 ? (
          <p className="text-sm text-zinc-600" data-testid="digest-ab-empty">
            No digest sends recorded yet — the experiment starts next Monday.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {["A", "B"].filter((c) => ab[c]).map((c) => (
              <div key={c} className="border border-white/[0.08] p-4" data-testid={`digest-ab-cohort-${c}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">
                  Cohort {c} · {c === "A" ? "Mon 9am ET" : "Mon 4pm ET"}
                </div>
                <div className="font-display text-3xl font-black mt-1">
                  {Math.round((ab[c].engagement_rate || 0) * 100)}%
                  <span className="text-sm text-zinc-500 font-normal"> engaged</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  {ab[c].engaged_24h} of {ab[c].sends} digests drove a login · {ab[c].sms || 0} sms / {ab[c].email || 0} email
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
